/**
 * Tiny SVG line chart — zero deps. Used to plot the trend of the
 * last ~20 readings of one counter on EquipmentDetailPage. Keeps
 * the bundle small (recharts isn't installed).
 *
 * Behaviour:
 *   - Auto-fits to the container width (set via the `width` prop).
 *   - Renders a polyline + dots + min/max ticks on the Y axis.
 *   - Empty input → returns null so the parent can show its own
 *     empty state.
 */
interface Point {
  x: number; // ms timestamp
  y: number;
}

interface MiniLineChartProps {
  points: Point[];
  width?: number;
  height?: number;
  unit?: string;
}

export function MiniLineChart({
  points,
  width = 280,
  height = 80,
  unit,
}: MiniLineChartProps) {
  if (points.length < 2) return null;
  const padding = 6;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;

  const toCoords = (p: Point) => ({
    cx: padding + ((p.x - xMin) / xRange) * innerW,
    cy: padding + (1 - (p.y - yMin) / yRange) * innerH,
  });

  const polyPoints = points
    .map((p) => {
      const { cx, cy } = toCoords(p);
      return `${cx.toFixed(1)},${cy.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Trend"
      className="text-emerald-600"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        points={polyPoints}
      />
      {points.map((p) => {
        const { cx, cy } = toCoords(p);
        return (
          <circle
            key={`${p.x}-${p.y}`}
            cx={cx}
            cy={cy}
            r={1.8}
            fill="currentColor"
          />
        );
      })}
      <text
        x={padding}
        y={padding + 6}
        fontSize="9"
        fill="currentColor"
        opacity={0.7}
      >
        {yMax.toFixed(0)} {unit ?? ''}
      </text>
      <text
        x={padding}
        y={height - padding}
        fontSize="9"
        fill="currentColor"
        opacity={0.7}
      >
        {yMin.toFixed(0)} {unit ?? ''}
      </text>
    </svg>
  );
}
